<template>
  <OverlayScrollbarsComponent
    ref="osComponent"
    :options="{
      update: {
        elementEvents: [['img', 'load'], ['*', 'transitionend']]
      },
      scrollbars: {
        theme: 'os-theme-lms',
        clickScroll: true,
        autoHide: autoHide ? 'leave' : 'never',
        visibility: isVisible ? 'auto' : 'hidden'
      }
    }"
    class="sui-scroll-vue3" :class="`sui-scroll-vue3--size-${currentSize}`"
    @os-initialized="onInitialized"
    @os-scroll="(_, event) => $emit('scroll', event)"
  >
    <slot></slot>
  </OverlayScrollbarsComponent>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import type { PropType } from 'vue'
import { OverlayScrollbars, ClickScrollPlugin } from 'overlayscrollbars'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue'

const props = defineProps({
  size: {
    type: String as PropType<'S' | 'M' | 'auto'>,
    default: 'auto'
  },
  autoHide: {
    type: Boolean,
    default: false
  },
  isVisible: {
    type: Boolean,
    default: true
  }
})

defineEmits<{
  scroll: [event: Event]
}>()

defineSlots<{
  default(): any
}>()

OverlayScrollbars.plugin(ClickScrollPlugin)

const osComponent = ref<InstanceType<typeof OverlayScrollbarsComponent> | null>(null)

// Размеры

const currentSize = ref<'S' | 'M' | null>(null)

const recalcCurrentSize = () => {
  if (props.size === 'auto') {
    currentSize.value = window.innerWidth < 1920 ? 'S' : 'M'
  } else {
    currentSize.value = props.size
  }
}

recalcCurrentSize()

onMounted(() => window.addEventListener('resize', recalcCurrentSize))
onBeforeUnmount(() => window.removeEventListener('resize', recalcCurrentSize))

// Фокус

const onInitialized = (instance: OverlayScrollbars) => {
  const { viewport } = instance.elements()
  viewport.setAttribute('tabindex', '0')
}

// Интерфейс

const getElement = () => osComponent.value?.osInstance()?.elements().viewport

defineExpose({
  getElement
})
</script>

<style lang="scss">
.sui-scroll-vue3 {
  // Изолируем стили во избежание конфликта с версией библиотеки для Vue 2
  @import 'node_modules/overlayscrollbars/styles/overlayscrollbars';

  /**
   * hide native scrollbars
   * changes to this styles need to be reflected in the environment styles to correctly detect scrollbar hiding
   */

  &[data-overlayscrollbars-initialize] {
    scrollbar-width: none !important;
  }
  &[data-overlayscrollbars-initialize]::-webkit-scrollbar,
  &[data-overlayscrollbars-initialize]::-webkit-scrollbar-corner {
    appearance: none !important;
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  /**
   * elements wont suddenly clip after initialization is done
   */

  &[data-overlayscrollbars-initialize]:not([data-overlayscrollbars]):not(html):not(body) {
    overflow: auto;
  }

  /**
   * structure setup 
   */
  &[data-overlayscrollbars] {
    position: relative;
  }

  &[data-overlayscrollbars~='host'] { // `~="host"` is only needed because `[data-overlayscrollbars]:not([data-overlayscrollbars-viewport])` specificity is too high
    display: flex;
    align-items: stretch !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    scroll-behavior: auto !important;
  }

  &[data-overlayscrollbars] {
    overflow: hidden !important;
  }

  &[data-overlayscrollbars~='noClipping'] {
    overflow: visible !important;
  }

  // NOTE: Не используем v-bind из-за конфликта стилей между разными копиями кита

  &--size-S > .os-theme-lms {
    --os-size: 12px;
    --os-handle-perpendicular-size: 4px;
    --os-handle-perpendicular-size-hover: 8px;
    --os-handle-perpendicular-size-active: 8px;
  }

  &--size-M > .os-theme-lms {
    --os-size: 16px;
    --os-handle-perpendicular-size: 8px;
    --os-handle-perpendicular-size-hover: 12px;
    --os-handle-perpendicular-size-active: 12px;
  }

  & > .os-theme-lms {
    --os-handle-bg: var(--stroke-select);
    --os-handle-bg-hover: var(--stroke-select);
    --os-handle-bg-active: var(--stroke-select);
    --os-handle-border-radius: 20px;
    --os-handle-min-size: 40px;

    @media (hover: none) {
      --os-handle-perpendicular-size-hover: var(--os-handle-perpendicular-size);
      --os-handle-perpendicular-size-active: var(--os-handle-perpendicular-size);
    }

    /**
     * NOTE: По непонятной причине в библиотеке прописаны 2 конфликтующих правила,
     * что делает невозможным использование переменной --os-track-border:
     * 
     * .os-scrollbar-track {
     *   border: none !important;
     * }
     * 
     * .os-scrollbar-track {
     *   border: var(--os-track-border);
     * }
     *
     * В любом случае, переменная не позволяет задать рамку только вдоль трека.
     */

    &.os-scrollbar-vertical {
      & > .os-scrollbar-track {
        border-left: 1px solid transparent !important;
        border-right: 1px solid transparent !important;

        &:hover,
        &:active {
          border-left: 1px solid var(--stroke-cards) !important;
          border-right: 1px solid var(--stroke-cards) !important;
        }

        @media (hover: none) {
          border: none !important;
        }

        & > .os-scrollbar-handle {
          margin-right: 1px;
        }
      }
    }

    &.os-scrollbar-horizontal {
      & > .os-scrollbar-track {
        border-top: 1px solid transparent !important;
        border-bottom: 1px solid transparent !important;

        &:hover,
        &:active {
          border-top: 1px solid var(--stroke-cards) !important;
          border-bottom: 1px solid var(--stroke-cards) !important;
        }

        @media (hover: none) {
          border: none !important;
        }

        & > .os-scrollbar-handle {
          margin-bottom: 1px;
        }
      }
    }
  }
}

// Выносим объявления переменных из overlayscrollbars.css, тк они должны быть заданы глобально

@property --os-scroll-percent {
  syntax: '<number>';
  inherits: true;
  initial-value: 0;
}

@property --os-viewport-percent {
  syntax: '<number>';
  inherits: true;
  initial-value: 0;
}
</style>
